import React from "react";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../components/layout/header/Header";
import {
  DashboardCompletionStepBox,
  ExpandablePanel,
  VerticalProgressBarCirclesWithText,
} from "@truevault/aquilo";
import { RouteHelpers, Routes } from "../../root/routes";
import { usePrimaryOrganizationId } from "../../hooks";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";

export const DevInstructionsFinishedApiIntegrationsSetupSlug = "finished-api-integrations-setup";
export const DevInstructionsFinishedWorkflowSetupSlug = "finished-workflow-setup";
export const DevInstructionsFinishedDataStorageSettingsSetupSlug =
  "finished-data-storage-settings-setup";

export default function WorkflowsDashboardPage() {
  const orgId = usePrimaryOrganizationId();

  const [onboardingProgress] = useOnboardingProgress(orgId);
  const {
    workflowsSetupOnboardingProgress: {
      isFinishedWithApiIntegrationsSetup,
      isFinishedDataStorageOnboarding,
      isFinishedWorkflowSetupOnboarding,
      isLimitSettingsComplete,
    },
  } = onboardingProgress || {};

  let isDataStorageItemEnabled =
    isFinishedWithApiIntegrationsSetup || isFinishedDataStorageOnboarding;

  let isFinishedWithWorkflowSetup = isFinishedWorkflowSetupOnboarding && isLimitSettingsComplete;

  let isWorkflowSetupItemEnabled = isFinishedWithWorkflowSetup || isFinishedDataStorageOnboarding;

  // If this org has already completed the onboarding setup for workflows, but they have a missing response for the limit workflow, then take them to the limit workflow setup page directly
  let defaultWorkflowSettingsRoute =
    isFinishedWorkflowSetupOnboarding && !isLimitSettingsComplete
      ? Routes.organization.requestHandling.requestToLimit
      : Routes.organization.requestHandling.base;

  let isEmailTemplatesItemEnabled = isFinishedWorkflowSetupOnboarding;

  let workflowSetupItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/request-workflows-icon.svg"
      title="Workflow Setup"
      description="Automate your request processing"
      status={(() => {
        if (isFinishedWithWorkflowSetup) {
          return "complete";
        }

        if (isFinishedWorkflowSetupOnboarding) {
          return isLimitSettingsComplete ? "complete" : "finish-set-up";
        } else {
          return isFinishedDataStorageOnboarding ? "finish-set-up" : "disabled";
        }
      })()}
    />
  );

  let dataStorageSettingsItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/data-storage-icon.svg"
      title="Data Storage"
      description="Set up your vendors data access settings"
      status={(() => {
        if (isFinishedDataStorageOnboarding) {
          return "complete";
        }
        return isDataStorageItemEnabled ? "finish-set-up" : "disabled";
      })()}
    />
  );

  let emailTemplatesItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/email-templates-icon.svg"
      title="Email Templates"
      description="View and customize your message templates"
      status={isFinishedWorkflowSetupOnboarding ? "complete" : "disabled"}
    />
  );

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="DSAR Workflows" />
      <div className="tw-boundary">
        {(!isFinishedWorkflowSetupOnboarding ||
          !isFinishedDataStorageOnboarding ||
          !isFinishedWithApiIntegrationsSetup) && (
          <ExpandablePanel title="Finish Setting Up DSAR Workflows" titleFontSize="2xl">
            <>
              <p className="font-normal text-[18px] text-neutral-800 mt-6">
                Complete the steps below to set up your requests workflow.
              </p>
              <div className="mt-6">
                <VerticalProgressBarCirclesWithText
                  steps={[
                    {
                      name: "API Integrations",
                      description: "Connect your vendors to TrueVault",
                      status: isFinishedWithApiIntegrationsSetup ? "complete" : "current",
                      route: Routes.privacyCenter.integrations,
                    },
                    {
                      name: "Data Storage",
                      description: "Set up your vendors data access settings",
                      status: isFinishedDataStorageOnboarding
                        ? "complete"
                        : isFinishedWithApiIntegrationsSetup
                        ? "current"
                        : "upcoming",
                      route: Routes.workflows.dataStorageSettings,
                    },
                    {
                      name: "Workflow Setup",
                      description: "Automate your request processing",
                      status: isFinishedWithWorkflowSetup
                        ? "complete"
                        : isFinishedDataStorageOnboarding
                        ? "current"
                        : "upcoming",
                      route: RouteHelpers.organization.requestHandling.base(orgId),
                    },
                  ]}
                />
              </div>
            </>
          </ExpandablePanel>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            maxWidth: "830px",
            marginTop: "48px",
          }}
        >
          <a href={Routes.privacyCenter.integrations}>
            <DashboardCompletionStepBox
              iconFilename="/assets/images/stay_compliant/privacy_center/icons/api-integrations-icon.svg"
              title="API Integrations"
              description="Connect your vendors to TrueVault"
              status={isFinishedWithApiIntegrationsSetup ? "complete" : "finish-set-up"}
            />
          </a>

          {isDataStorageItemEnabled ? (
            <a href={Routes.workflows.dataStorageSettings}>{dataStorageSettingsItem}</a>
          ) : (
            dataStorageSettingsItem
          )}

          {isWorkflowSetupItemEnabled ? (
            <a href={defaultWorkflowSettingsRoute}>{workflowSetupItem}</a>
          ) : (
            workflowSetupItem
          )}

          {isEmailTemplatesItemEnabled ? (
            <a href={Routes.workflows.emailTemplates}>{emailTemplatesItem}</a>
          ) : (
            emailTemplatesItem
          )}
        </div>
      </div>
    </StandardPageWrapper>
  );
}
